import React, { useEffect, useMemo, useState } from 'react';
import { SkeletonLines } from '@/components/Admin/UI/Skeleton';
import styled from 'styled-components';
import { colors } from '@/constants/colors';
import { toast } from 'react-toastify';
import Btn from '../UI/Btn';
import DurationUnitInput from '../UI/DurationUnitInput';
import { useQueryClient } from '@tanstack/react-query';
import { useUIStore } from '@/store/useUIStore';
import { useVendoSessionsStore } from '@/store/useVendoSessionsStore';
import { useVendoDevicesStore } from '@/store/useVendoDevicesStore';
import { extendSession, grantSession, addSessionData, grantSessionData, setWhitelistScope } from '@/api/vendo';

export interface ManageAccessData {
   session: {
      // Empty when opened from the "Awaiting" tab — the device has joined
      // the WiFi but has no session yet, so "Add time" grants a fresh
      // operator session (grantSession) instead of extending one.
      id: string;
      mac: string;
      // vendoDevice ObjectId — required for the permanent (whitelist) path
      // AND for the awaiting grant path (no session id to key off).
      deviceId: string | null;
      deviceName: string;
      // Lease IP, when known (Awaiting tab) — passed to the router grant.
      ip?: string;
      /** Vendos this phone is ALREADY Unlimited on. Set when the dialog is
       *  opened from an existing Unlimited row (Edit) so the tick list opens
       *  showing reality instead of the all-ticked default. */
      whitelistDeviceIds?: string[];
      /** The row is a DATA session (cap in MB). */
      isDataSession?: boolean;
      /** …and it is still active or paused, so MB can be ADDED to it. An
       *  ended row (or a time row) on a data-rate vendo gets a fresh data
       *  grant instead. */
      liveDataSession?: boolean;
   };
   /** Open straight on the Permanent tab — the Edit action on an Unlimited
    *  row has nothing to do with extending time. */
   initialMode?: 'extend' | 'permanent';
}

interface Props {
   data: ManageAccessData;
}

type Mode = 'extend' | 'permanent';

const ManageAccess = ({ data }: Props) => {
   const { handleClose } = useUIStore();
   const refetchSessions = useVendoSessionsStore((s) => s.fetch);
   const refetchDevices = useVendoDevicesStore((s) => s.fetch);
   const queryClient = useQueryClient();

   const devices = useVendoDevicesStore((s) => s.devices);
   const devicesFetched = useVendoDevicesStore((s) => s.fetched);

   const { session } = data;
   // No session id → opened from the Awaiting tab (grant fresh time).
   const isAwaiting = !session.id;
   // DATA, not time (Ray 2026-09-15): a data session gets megabytes; an
   // awaiting phone on a data-rate vendo gets megabytes too.
   // The VENDO decides (Ray 2026-09-15): on a data-rate vendo the operator
   // gives data whatever the row is — an expired time row included.
   const vendoRateMode = devices.find((d) => d._id === session.deviceId)?.activeRateMode;
   const dataMode = session.isDataSession === true || vendoRateMode === 'data';
   // Add MB to the customer's live data session; otherwise start a fresh one.
   const topUpLive = !isAwaiting && session.liveDataSession === true;
   const [dataMbText, setDataMbText] = useState('1000');
   const [mode, setMode] = useState<Mode>(data.initialMode ?? 'extend');
   // Editing an EXISTING Unlimited phone. Giving it time is a DOWNGRADE, not
   // an addition: the two cannot coexist, or one phone shows as two active
   // rows (Ray hit exactly that on staging 2026-08-14). Granting Unlimited
   // already supersedes a running session, so this makes the reverse true and
   // the rule becomes symmetric — whichever the operator sets last wins, and a
   // phone is always exactly one row.
   //
   // It needs a confirm, unlike the other direction: this one DESTROYS a
   // permanent grant, and an accidental tap would quietly take a partner's
   // free internet away with nothing to undo it. Granting Unlimited over a
   // session only ever gives the customer more, so it stays silent.
   const editingUnlimited = data.initialMode === 'permanent';
   const [confirmDowngrade, setConfirmDowngrade] = useState(false);
   const vendoCountNow = session.whitelistDeviceIds?.length ?? 0;

   // The vendos an Unlimited phone can be made free on. Mains only: a
   // sub-vendo has no router of its own and is covered by the grant on its
   // main, so listing subs would offer a choice that changes nothing. Sold
   // devices are excluded — the operator can no longer grant on them.
   const vendoOptions = useMemo(
      () =>
         devices
            .filter((d) => !d.parent && d.isCurrentOwner !== false)
            .sort((a, b) => a.name.localeCompare(b.name)),
      [devices]
   );

   // Editing an existing grant opens on what is true today. A fresh grant
   // opens with every vendo ticked — an operator who wants it everywhere (the
   // common case) just saves, and one who does not unticks. Defaulting to
   // only the current vendo would silently keep today's broken behaviour.
   const [ticked, setTicked] = useState<Set<string>>(new Set());
   // Nothing ticked = nowhere to whitelist. The button is greyed rather than
   // answering with a toast: Ray un-ticked every vendo, pressed it, and read
   // the phone's missing internet as a router bug (staging hEX, 2026-10-15).
   const nothingTicked = mode === 'permanent' && vendoOptions.length > 0 && ticked.size === 0;
   const [tickedSeeded, setTickedSeeded] = useState(false);
   useEffect(() => {
      if (tickedSeeded || vendoOptions.length === 0) return;
      const existing = session.whitelistDeviceIds;
      setTicked(
         new Set(existing?.length ? existing : vendoOptions.map((d) => d._id))
      );
      setTickedSeeded(true);
   }, [vendoOptions, session.whitelistDeviceIds, tickedSeeded]);

   // The dialog can be opened before the devices list has ever loaded.
   useEffect(() => {
      if (!devicesFetched) void refetchDevices();
   }, [devicesFetched, refetchDevices]);

   const toggleVendo = (id: string) =>
      setTicked((prev) => {
         const next = new Set(prev);
         if (next.has(id)) next.delete(id);
         else next.add(id);
         return next;
      });
   // Minutes — DurationUnitInput stores in minutes and converts the
   // unit picker (minutes/hours/days/months) for us. Default 30 min.
   const [minutes, setMinutes] = useState<number | null>(30);
   // Validity (pause): how long paused time is kept. Default 1 day; 0 = for ever.
   // An operator grant has no rate, so without this it fell to the vendo's 24 h
   // whatever the operator intended (Ray, 2026-09-15).
   const [validity, setValidity] = useState<number | null>(1440);
   const [isLoading, setIsLoading] = useState(false);

   const handleConfirm = async () => {
      if (isLoading) return;

      if (mode === 'extend' && dataMode) {
         const dataMb = Math.floor(Number(dataMbText));
         if (!Number.isFinite(dataMb) || dataMb <= 0) {
            toast.error('Enter the megabytes to add. Example: 1000');
            return;
         }
         try {
            setIsLoading(true);
            if (topUpLive) {
               await addSessionData(session.id, dataMb);
               toast.success(`Added ${dataMb} MB to ${session.mac}`, { autoClose: 2000 });
            } else {
               if (!session.deviceId) {
                  toast.error('This device has no linked vendo — cannot give data');
                  return;
               }
               await grantSessionData(session.deviceId, session.mac, dataMb, session.ip, validity ?? 1440);
               toast.success(`Gave ${session.mac} ${dataMb} MB of data`, { autoClose: 2500 });
            }
            await refetchSessions();
            await queryClient.invalidateQueries({ queryKey: ['vendo-awaiting-leases'] });
            handleClose();
         } catch (err: any) {
            toast.error(err?.response?.data?.error || 'Failed to add data');
         } finally {
            setIsLoading(false);
         }
         return;
      }

      if (mode === 'extend') {
         if (!minutes || minutes <= 0) {
            toast.error('Enter a positive amount of time');
            return;
         }
         // Destroying a permanent grant gets one deliberate second press.
         if (editingUnlimited && !confirmDowngrade) {
            setConfirmDowngrade(true);
            return;
         }
         try {
            setIsLoading(true);
            if (editingUnlimited) {
               // Downgrade: strip Unlimited from EVERY vendo first, so the
               // timed session below is the phone's only access and the list
               // shows one row. Doing it the other way round would leave the
               // phone free anyway and make the time meaningless.
               await setWhitelistScope(session.mac, []);
            }
            if (isAwaiting) {
               if (!session.deviceId) {
                  toast.error('This device has no linked vendo — cannot grant time');
                  return;
               }
               await grantSession(session.deviceId, session.mac, minutes, session.ip, validity ?? 1440);
               toast.success(`Gave ${session.mac} ${minutes} min of internet`, {
                  autoClose: 2500,
               });
            } else {
               await extendSession(session.id, minutes, validity ?? undefined);
               toast.success(`Added time to ${session.mac}`, { autoClose: 2000 });
            }
            await refetchSessions();
            await queryClient.invalidateQueries({ queryKey: ['vendo-awaiting-leases'] });
            handleClose();
         } catch (err: any) {
            toast.error(
               err?.response?.data?.error ||
                  (isAwaiting ? 'Failed to grant time' : 'Failed to extend session')
            );
         } finally {
            setIsLoading(false);
         }
         return;
      }

      // Permanent = free unlimited internet on every ticked vendo.
      if (!session.deviceId) {
         toast.error('This session has no linked device — cannot whitelist');
         return;
      }
      if (ticked.size === 0) {
         toast.error('Pick at least one vendo, or use Kick to remove it everywhere');
         return;
      }
      try {
         setIsLoading(true);
         const res = await setWhitelistScope(session.mac, [...ticked]);
         // A vendo whose router did not answer is saved but not live yet. Say
         // so plainly — it is not an error, and hiding it would leave the
         // operator thinking the phone works somewhere it does not, yet.
         const pending = res.results.filter(
            (r) => r.action !== 'unchanged' && !r.applied
         );
         const where =
            res.vendoCount === 1
               ? 'this vendo'
               : `${res.vendoCount} vendos`;
         if (pending.length > 0) {
            toast.success(
               `${session.mac} is now unlimited on ${where}. ${pending.length} offline — ${pending
                  .map((p) => p.name)
                  .join(', ')} will apply when back.`,
               { autoClose: 6000 }
            );
         } else {
            toast.success(`${session.mac} is now unlimited on ${where}`, {
               autoClose: 2500,
            });
         }
         // Whitelist lives on the device doc; refresh both so the device
         // settings list and the session view reflect it.
         await Promise.all([refetchDevices(), refetchSessions()]);
         await queryClient.invalidateQueries({ queryKey: ['vendo-awaiting-leases'] });
         handleClose();
      } catch (err: any) {
         toast.error(
            err?.response?.data?.error || 'Failed to whitelist device'
         );
      } finally {
         setIsLoading(false);
      }
   };

   return (
      <Wrapper>
         <DeviceLine>
            <DeviceMac>{session.mac}</DeviceMac>
            <DeviceName>{session.deviceName}</DeviceName>
         </DeviceLine>

         {(
            <ModeToggle>
               <ModeBtn
                  $active={mode === 'extend'}
                  onClick={() => setMode('extend')}
                  type="button"
               >
                  {dataMode ? 'Add data' : 'Extend time'}
               </ModeBtn>
               <ModeBtn
                  $active={mode === 'permanent'}
                  onClick={() => setMode('permanent')}
                  type="button"
               >
                  Permanent
               </ModeBtn>
            </ModeToggle>
         )}

         {mode === 'extend' ? (
            <Field>
               {editingUnlimited && (
                  <DowngradeNote $armed={confirmDowngrade}>
                     {confirmDowngrade ? (
                        <>
                           <b>Press again to confirm.</b> {session.mac} loses
                           Unlimited on{' '}
                           {vendoCountNow === 1 ? 'this vendo' : `all ${vendoCountNow} vendos`}{' '}
                           and gets timed internet instead.
                        </>
                     ) : (
                        <>
                           This phone has <b>Unlimited</b>
                           {vendoCountNow > 1 ? ` on ${vendoCountNow} vendos` : ''}.
                           Giving it time <b>removes Unlimited everywhere</b> and
                           replaces it with a timed session.
                        </>
                     )}
                  </DowngradeNote>
               )}
               <TwoCol>
                  <Col>
                     <Label>{dataMode ? (topUpLive ? 'Add data (MB)' : 'Give data (MB)') : isAwaiting ? 'Give time' : 'Add time'}</Label>
                     {dataMode ? (
                        <MbInput
                           type="text"
                           inputMode="numeric"
                           value={dataMbText}
                           onChange={(e) => setDataMbText(e.target.value.replace(/[^0-9]/g, ''))}
                           placeholder="1000"
                        />
                     ) : (
                        <DurationUnitInput value={minutes} onChange={setMinutes} min={1} />
                     )}
                     <Hint>
                        {dataMode
                           ? !topUpLive
                              ? 'Starts a data session for this device from the vendo\u2019s plan. Example: 1000 = 1 GB.'
                              : 'Megabytes are added to the customer\u2019s current data. Example: 1000 = 1 GB.'
                           : isAwaiting
                           ? 'Starts a free session for this device — no coin needed. Good for a TV or device that can’t open the portal.'
                           : "Time is added to the customer's current session."}
                     </Hint>
                  </Col>
                  {!(dataMode && topUpLive) && (
                  <Col>
                     <Label>Validity (pause)</Label>
                     <DurationUnitInput value={validity} onChange={setValidity} min={0} />
                     <Hint>
                        How long paused time is kept. Example: 7 days. Type 0 to keep it for
                        ever.
                     </Hint>
                  </Col>
                  )}
               </TwoCol>
            </Field>
         ) : (
            <Field>
               <PermNote>
                  Gives <strong>{session.mac}</strong> free <strong>permanent</strong>{' '}
                  internet — it skips the payment screen and never expires. Use this
                  for a partner / host device.
               </PermNote>
               <Label>Free on these vendos</Label>
               <VendoList>
                  {vendoOptions.length === 0 ? (
                     <SkeletonLines lines={3} />
                  ) : (
                     vendoOptions.map((d) => (
                        <VendoRow key={d._id}>
                           <input
                              type="checkbox"
                              checked={ticked.has(d._id)}
                              onChange={() => toggleVendo(d._id)}
                           />
                           <VendoName>{d.name}</VendoName>
                           {/* ROUTER liveness, not `status`. `status` is the
                               COIN BOX heartbeat — a vendo whose coin board is
                               dark but whose router is beating reads "offline"
                               there while it is perfectly able to let this
                               phone through. Caught on staging 2026-08-14: STA
                               CLOUD RUIJIE showed offline with a 0-minute-old
                               router heartbeat. What matters here is only
                               whether the ROUTER can apply the grant. */}
                           {d.routerStatus !== 'connected' && (
                              <OfflineTag>offline</OfflineTag>
                           )}
                        </VendoRow>
                     ))
                  )}
               </VendoList>
               <TickActions>
                  <LinkBtn
                     type="button"
                     onClick={() => setTicked(new Set(vendoOptions.map((d) => d._id)))}
                  >
                     Select all
                  </LinkBtn>
                  <LinkBtn
                     type="button"
                     onClick={() =>
                        setTicked(
                           new Set(session.deviceId ? [session.deviceId] : [])
                        )
                     }
                  >
                     This vendo only
                  </LinkBtn>
               </TickActions>
               <Hint>
                  {nothingTicked
                     ? 'Tick at least one vendo — with none ticked there is nowhere to whitelist it.'
                     : 'An offline vendo is still saved — it starts working when the vendo comes back. Remove the phone anytime from the Unlimited row.'}
               </Hint>
            </Field>
         )}

         <BtnRow>
            <div onClick={handleClose}>
               <Btn title="Cancel" type="outline" size="small" />
            </div>
            <div onClick={nothingTicked ? undefined : handleConfirm}>
               <Btn
                  title={
                     isLoading
                        ? 'Working...'
                        : mode === 'extend'
                          ? editingUnlimited
                             ? confirmDowngrade
                                ? 'Yes, remove Unlimited'
                                : 'Remove Unlimited & give time'
                             : dataMode
                               ? !topUpLive
                                  ? 'Give data'
                                  : 'Add data'
                               : isAwaiting
                                 ? 'Give time'
                                 : 'Add time'
                          : 'Whitelist device'
                  }
                  type={isLoading || nothingTicked ? 'disabled' : 'normal'}
                  size="small"
                  bgcolor={
                     isLoading || nothingTicked
                        ? colors.GRAY10
                        : confirmDowngrade
                          ? colors.WARNING
                          : colors.PRIMARY
                  }
                  textcolor={isLoading || nothingTicked ? colors.GRAY1 : colors.WHITE}
               />
            </div>
         </BtnRow>
      </Wrapper>
   );
};

const Wrapper = styled.div`
   width: 460px;
   max-width: 100%;
   display: flex;
   flex-direction: column;
   gap: 16px;
   @media (max-width: 768px) {
      width: 100%;
   }
`;
const DeviceLine = styled.div`
   display: flex;
   align-items: baseline;
   gap: 10px;
   padding: 12px 14px;
   border-radius: 10px;
   background: var(--app-brand-tint);
`;
const DeviceMac = styled.span`
   font-size: 16px;
   font-weight: 800;
   font-family: monospace;
   color: ${colors.PRIMARY};
`;
const DeviceName = styled.span`
   font-size: 13px;
   color: ${colors.GRAY4};
`;
const ModeToggle = styled.div`
   display: flex;
   border: 1px solid ${colors.GRAY12};
   border-radius: 8px;
   overflow: hidden;
`;
const ModeBtn = styled.button<{ $active: boolean }>`
   flex: 1;
   padding: 10px 12px;
   border: none;
   font-size: 14px;
   font-weight: 600;
   cursor: pointer;
   background: ${({ $active }) => ($active ? colors.PRIMARY : 'var(--app-surface)')};
   color: ${({ $active }) => ($active ? colors.WHITE : colors.GRAY3)};
   transition: background 0.15s;
`;
const Field = styled.div`
   display: flex;
   flex-direction: column;
   gap: 8px;
`;
/* The two duration fields side by side — one field per half, no empty half
   (Ray, 2026-09-15). Stack on a phone. */
const TwoCol = styled.div`
   display: grid;
   grid-template-columns: 1fr 1fr;
   gap: 16px;
   @media (max-width: 560px) {
      grid-template-columns: 1fr;
   }
`;
const Col = styled.div`
   display: flex;
   flex-direction: column;
   gap: 8px;
   min-width: 0;
`;
const MbInput = styled.input`
   width: 100%;
   height: 44px;
   padding: 0 12px;
   border: 1px solid ${colors.GRAY12};
   border-radius: 10px;
   font-size: 16px;
   font-weight: 600;
   text-align: right;
   background: var(--app-surface, #fff);
   color: inherit;
   &:focus { outline: none; border-color: ${colors.BLUE}; }
`;

const Label = styled.label`
   font-size: 13px;
   color: ${colors.GRAY1};
   font-weight: 600;
`;
const PermNote = styled.p`
   font-size: 14px;
   line-height: 1.5;
   color: ${colors.GRAY2};
`;
// Amber, not red: the operator asked for this. It turns firm on the second
// press, when the next tap actually destroys the grant.
const DowngradeNote = styled.p<{ $armed?: boolean }>`
   font-size: 13px;
   line-height: 1.5;
   padding: 10px 12px;
   border-radius: 8px;
   color: ${({ $armed }) => ($armed ? colors.WHITE : colors.GRAY1)};
   background: ${({ $armed }) => ($armed ? colors.WARNING : 'var(--app-brand-tint)')};
`;

const Hint = styled.span`
   font-size: 12px;
   color: ${colors.GRAY5};
`;
// Scrolls rather than grows: an operator with 40 vendos must not push the
// Save button off a short laptop screen.
const VendoList = styled.div`
   display: flex;
   flex-direction: column;
   gap: 2px;
   max-height: 190px;
   overflow-y: auto;
   border: 1px solid ${colors.GRAY12};
   border-radius: 8px;
   padding: 8px 10px;
`;
const VendoRow = styled.label`
   display: flex;
   align-items: center;
   gap: 8px;
   padding: 4px 0;
   font-size: 13px;
   color: ${colors.GRAY2};
   cursor: pointer;
`;
const VendoName = styled.span`
   flex: 1;
   overflow: hidden;
   text-overflow: ellipsis;
   white-space: nowrap;
`;
const OfflineTag = styled.span`
   font-size: 11px;
   color: ${colors.GRAY5};
`;
const TickActions = styled.div`
   display: flex;
   gap: 14px;
`;
const LinkBtn = styled.button`
   background: none;
   border: none;
   padding: 0;
   font-size: 12px;
   font-weight: 600;
   color: ${colors.PRIMARY};
   cursor: pointer;
`;
const BtnRow = styled.div`
   display: flex;
   justify-content: flex-end;
   gap: 10px;
   margin-top: 4px;
`;

export default ManageAccess;
